import testService from 'ApiService/Teacher/TestService';
import batchTeacherStructureService from 'ApiService/Teacher/batch.teacher.service';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const AddInfo = ({
  setDisable,
  handleInputChange,
  formData,
  setFormData,
  resetFlag,
}) => {
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [batchStructures, setBatchStructures] = useState([]);
  const [loadingStructures, setLoadingStructures] = useState(false);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [errors, setErrors] = useState([]);
  const { state } = useContext(AuthContext);
  const selectedBranch = state?.user?.branch?._id || state?.user?.branch?.name;

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (resetFlag) {
      setFormData((prev) => ({
        ...prev,
        courseId: '',
        batchStructureId: '',
        batchId: '',
        title: '',
        url: '',
        description: '',
      }));
      setBatchStructures([]);
      setBatches([]);
    }
  }, [resetFlag, setFormData]);

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await testService.getAllCourses();
      if (response.data.success) {
        setCourses(response.data.courses);
      } else {
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleCourseChange = async (event) => {
    const courseId = event.target.value;

    setFormData((prev) => ({
      ...prev,
      courseId,
      batchStructureId: '',
      batchId: '',
    }));

    setBatchStructures([]);
    setBatches([]);

    if (!courseId) return;

    try {
      setLoadingStructures(true);

      const res = await batchTeacherStructureService.getBatchStructuresByCourse(
        courseId
      );

      if (res.data.success) {
        setBatchStructures(res.data.batchStructures || []);
      }
    } catch (err) {
      toast.error('Failed to load batch structures');
    } finally {
      setLoadingStructures(false);
    }
  };

  const handleBatchStructureChange = async (event) => {
    const batchStructureId = event.target.value;

    setFormData((prev) => ({
      ...prev,
      batchStructureId,
      batchId: '',
    }));

    setBatches([]);

    if (!batchStructureId) return;

    try {
      setLoadingBatches(true);

      const res =
        await batchTeacherStructureService.getBatchesByCourseStructure(
          formData.courseId,
          batchStructureId,
          selectedBranch
        );

      if (res.data.success) {
        setBatches(res.data.batches || []);
      }
    } catch (err) {
      toast.error('Failed to load batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleBatchChange = (event) => {
    const batchId = event.target.value;
    setFormData((prev) => ({
      ...prev,
      batchId,
    }));
  };

  useEffect(() => {
    const newErrors = [];
    if (
      !formData.courseId ||
      !formData.batchStructureId ||
      !formData.batchId ||
      !formData.title ||
      !formData.url
    ) {
      newErrors.push('Please fill in all required fields.');
    }
    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [formData, setDisable]);

  return (
    <div className="rounded-[20px] p-4">
      <h4 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
        Assign Training Material URL
      </h4>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="text-sm font-bold text-navy-700 dark:text-white">
              Course*
            </label>
            <select
              name="courseId"
              value={formData.courseId}
              onChange={handleCourseChange}
              className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Select Course</option>
              {loadingCourses ? (
                <option>Loading courses...</option>
              ) : courses.length === 0 ? (
                <option className="cursor-pointer font-bold text-brand-500">
                  No courses available
                </option>
              ) : (
                courses.map((course, i) => (
                  <option key={course._id} value={course._id}>
                    {i + 1}. {course.courseName}
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <label className="text-sm font-bold text-navy-700 dark:text-white">
              Batch Structure*
            </label>
            <select
              name="batchStructureId"
              value={formData.batchStructureId}
              onChange={handleBatchStructureChange}
              disabled={!formData.courseId}
              className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Select Batch Structure</option>
              {loadingStructures ? (
                <option>Loading...</option>
              ) : (
                batchStructures.map((bs) => (
                  <option key={bs._id} value={bs._id}>
                    {bs.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <label className="text-sm font-bold text-navy-700 dark:text-white">
              Batch*
            </label>
            <select
              name="batchId"
              value={formData.batchId}
              onChange={handleBatchChange}
              disabled={!formData.batchStructureId}
              className="mt-2 w-full md:min-w-[400px] rounded-xl border border-gray-200 p-3 text-sm outline-none disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Select Batch</option>
              {loadingBatches ? (
                <option>Loading...</option>
              ) : batches.length > 0 ? (
                batches.map((b) => {
                  const batchDisplay = `B${b.batchNumber}`;
                  
                  const date = new Date(b.startDate);
                  const day = date.getDate().toString().padStart(2, '0');
                  const month = date.toLocaleString('default', { month: 'short' });
                  const year = date.getFullYear().toString().slice(-2);
                  const dateDisplay = `${day}${month}${year}`;

                  const dayMap = {
                    Monday: 'M',
                    Tuesday: 'T',
                    Wednesday: 'W',
                    Thursday: 'Th',
                    Friday: 'F',
                    Saturday: 'S',
                    Sunday: 'Su',
                  };

                  const scheduleDisplay = b.daySchedules
                    ?.map((schedule) => {
                      const dayShort = dayMap[schedule.day] || schedule.day;
                      const time = schedule.startTime.replace(':00', '').replace(' ', '');
                      return `${dayShort}-${time}`;
                    })
                    .join(' | ');

                  const branchInitials = b.branches
                    ?.map((br) => br.name.split(',').pop().trim()[0])
                    .join('');

                  const schedulePart = scheduleDisplay ? `(${scheduleDisplay} )` : '';
                  const fullDisplay = `${batchDisplay} | ${dateDisplay} ${schedulePart} ${branchInitials || ''}`.trim();

                  return (
                    <option key={b._id} value={b._id}>
                      {fullDisplay}
                    </option>
                  );
                })
              ) : (
                <option>No batches found</option>
              )}
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm font-bold text-navy-700 dark:text-white">
            Title*
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
            placeholder="Enter URL title"
          />
        </div>

        <div>
          <label className="text-sm font-bold text-navy-700 dark:text-white">
            URL*
          </label>
          <input
            type="url"
            name="url"
            value={formData.url}
            onChange={handleInputChange}
            className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
            placeholder="https://example.com"
          />
        </div>

        <div>
          <label className="text-sm font-bold text-navy-700 dark:text-white">
            Description (Optional)
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
            placeholder="Enter description"
            rows="3"
          />
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mt-2 text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddInfo;
